import COUNTRY_LIST from './countries.json';

/** The same rules the server checks (TMS_server kyc-validation.ts), plus formatting as the customer types. */
export const digits = (v: string) => v.replace(/\D/g, '');

const VD = [[0,1,2,3,4,5,6,7,8,9],[1,2,3,4,0,6,7,8,9,5],[2,3,4,0,1,7,8,9,5,6],[3,4,0,1,2,8,9,5,6,7],[4,0,1,2,3,9,5,6,7,8],[5,9,8,7,6,0,4,3,2,1],[6,5,9,8,7,1,0,4,3,2],[7,6,5,9,8,2,1,0,4,3],[8,7,6,5,9,3,2,1,0,4],[9,8,7,6,5,4,3,2,1,0]];
const VP = [[0,1,2,3,4,5,6,7,8,9],[1,5,7,6,2,8,3,0,9,4],[5,8,0,3,7,9,6,1,4,2],[8,9,1,6,0,4,3,5,2,7],[9,4,5,3,1,2,6,8,7,0],[4,2,8,6,5,7,3,9,0,1],[2,7,9,3,8,0,6,4,1,5],[7,0,4,6,9,1,3,2,5,8]];
const verhoeff = (n: string) => {
  let c = 0;
  n.split("").reverse().forEach((d, i) => { c = VD[c][VP[i % 8][Number(d)]]; });
  return c === 0;
};
const saudiId = (n: string) => {
  let s = 0;
  n.split("").forEach((d, i) => {
    let x = Number(d);
    if (i % 2 === 0) { x *= 2; if (x > 9) x -= 9; }
    s += x;
  });
  return s % 10 === 0;
};

/** A field's state: error (blocks), warn (shown, doesn't block), ok, or nothing yet. */
export type Check = { error?: string; warn?: string; ok?: string };

export const formatAadhaar = (v: string) => digits(v).slice(0, 12).replace(/(\d{4})(?=\d)/g, '$1 ');
export const formatIqama = (v: string) => digits(v).slice(0, 10);
export const formatName = (v: string) => v.toUpperCase().replace(/[^A-Z .'-]/g, '');
export const formatPin = (v: string) => digits(v).slice(0, 6);
export function formatMobile(code: string, v: string) {
  const d = digits(v);
  if (code === '966') return d.slice(0, 9).replace(/^(\d{2})(\d{0,3})(\d{0,4}).*/, (_m, a, b, c) => [a, b, c].filter(Boolean).join(' '));
  if (code === '91') return d.slice(0, 10).replace(/^(\d{5})(\d{0,5}).*/, (_m, a, b) => (b ? `${a} ${b}` : a));
  return d.slice(0, 14);
}

export function checkAadhaar(v: string): Check {
  const d = digits(v);
  if (d.length < 12) return { error: d.length ? `Aadhaar has 12 digits — ${d.length} typed` : 'Enter the 12-digit Aadhaar number' };
  if (/^[01]/.test(d)) return { error: 'An Aadhaar number never starts with 0 or 1' };
  if (!verhoeff(d)) return { error: 'This number is not valid — check each digit' };
  return { ok: 'Looks right' };
}

export function checkIqama(v: string): Check {
  const d = digits(v);
  if (d.length < 10) return { error: d.length ? `Iqama has 10 digits — ${d.length} typed` : 'Enter the 10-digit Iqama number' };
  if (!/^[12]/.test(d) || !saudiId(d)) return { warn: 'Please check this number once more' };
  return { ok: 'Looks right' };
}

export const checkName = (v: string): Check =>
  v.trim().length < 3 ? { error: 'Write the full name as printed on the card' } : {};

export const checkAddress = (v: string): Check =>
  v.trim().length < 3 ? { error: 'Add house, street and town' } : {};

export function checkMobile(code: string, v: string): Check {
  const d = digits(v);
  if (code === '966') return d.length === 9 && d[0] === '5' ? {} : { error: 'A Saudi mobile has 9 digits starting with 5' };
  if (code === '91') return d.length === 10 && /^[6-9]/.test(d) ? {} : { error: 'An Indian mobile has 10 digits starting with 6, 7, 8 or 9' };
  const len = COUNTRIES.find((c) => c.dial === code)?.len;
  if (len) return d.length === len ? {} : { error: `This mobile has ${len} digits — ${d.length} typed` };
  return d.length >= 6 ? {} : { error: 'Check the mobile number' };
}

export const checkPin = (v: string): Check => {
  const d = digits(v);
  return d.length === 6 && d[0] !== '0' ? {} : { error: d.length ? `PIN code has 6 digits — ${d.length} typed` : 'Enter the 6-digit PIN code' };
};

export const required = (v: string, what: string): Check => (v.trim() ? {} : { error: `Add the ${what}` });

/** Name, address, mobile and PIN are optional: checked only once something is typed. */
export const ifTyped = (v: string, check: Check): Check => (v.trim() ? check : {});

/** Another ID (passport…): letters and digits as printed. */
export const formatOtherNumber = (v: string) => v.toUpperCase().replace(/[^A-Z0-9 -]/g, '').slice(0, 24);
export function checkOtherNumber(v: string): Check {
  const t = v.replace(/\s/g, '');
  if (!t) return { error: 'Enter the number on the ID' };
  return t.length >= 4 && t.length <= 20 ? {} : { error: 'Check the ID number — letters and digits as printed' };
}

/** The 128 countries TMS knows (same list as the TMS forms): name, ISO code, dial code, flag, mobile length. */
export type Country = { name: string; code: string; dial: string; flag: string; len: number | null };
export const COUNTRIES = COUNTRY_LIST as Country[];

